import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { FolderSimple, FilmStrip, Translate, TagSimple, Eye, Users, ChartBar } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatCard({ icon: Icon, label, value, color, testId }) {
  return (
    <div data-testid={testId} className="bg-[#141417] border border-[#2A2A2E] p-5 rounded-sm hover:border-[#3A3A3E] transition-colors duration-200">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA] font-bold">{label}</span>
        <Icon size={18} className={color} />
      </div>
      <div className="text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>{value}</div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [sR, aR] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/analytics/global?days=30`, { withCredentials: true }),
        ]);
        setStats(sR.data);
        setAnalytics(aR.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  if (loading) return (
    <div className="space-y-6 animate-pulse">
      <div className="h-8 bg-[#141417] rounded-sm w-48" />
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">{[1,2,3,4,5,6].map(i => <div key={i} className="h-28 bg-[#141417] rounded-sm border border-[#2A2A2E]" />)}</div>
    </div>
  );

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-white" style={{ fontFamily: 'Chivo, sans-serif' }}>Dashboard</h1>
        <p className="text-sm text-[#A1A1AA] mt-1">Global overview and analytics</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <StatCard icon={FolderSimple} label="Projects" value={stats?.projects_count || 0} color="text-[#4361EE]" testId="stat-projects" />
        <StatCard icon={FilmStrip} label="Contents" value={stats?.contents_count || 0} color="text-[#E53935]" testId="stat-contents" />
        <StatCard icon={TagSimple} label="Categories" value={stats?.categories_count || 0} color="text-emerald-400" testId="stat-categories" />
        <StatCard icon={Translate} label="Translated" value={stats?.translations_done || 0} color="text-amber-400" testId="stat-translated" />
        <StatCard icon={Eye} label="Total Views" value={analytics?.total_views || 0} color="text-[#4361EE]" testId="stat-total-views" />
        <StatCard icon={Users} label="30d Views" value={analytics?.recent_views || 0} color="text-[#E53935]" testId="stat-recent-views" />
      </div>

      {analytics?.project_views?.length > 0 && (
        <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
          <div className="px-5 py-4 border-b border-[#2A2A2E] flex items-center gap-2">
            <ChartBar size={16} className="text-[#4361EE]" />
            <h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Project Views (30 days)</h3>
          </div>
          <div className="p-5 space-y-3">
            {analytics.project_views.map((pv) => {
              const max = analytics.project_views[0]?.views || 1;
              const pct = Math.max((pv.views / max) * 100, 2);
              return (
                <div key={pv.id} className="group cursor-pointer" onClick={() => navigate(`/projects/${pv.id}`)}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm text-white font-medium group-hover:text-[#4361EE] transition-colors">{pv.name}</span>
                    <span className="text-xs font-bold text-[#A1A1AA]">{pv.views} views</span>
                  </div>
                  <div className="w-full bg-[#0A0A0C] rounded-sm h-2"><div className="bg-[#4361EE] h-2 rounded-sm transition-all" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
          <div className="px-5 py-4 border-b border-[#2A2A2E]"><h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Recent Projects</h3></div>
          <div className="divide-y divide-[#2A2A2E]">
            {stats?.recent_projects?.length > 0 ? stats.recent_projects.map((p, i) => (
              <div key={p.id} className={`px-5 py-3 flex items-center justify-between cursor-pointer hover:bg-[#1A1A1E] transition-colors animate-fade-up stagger-${i+1}`} onClick={() => navigate(`/projects/${p.id}`)}>
                <div>
                  <div className="text-sm text-white font-medium">{p.name}</div>
                  <div className="text-xs text-[#A1A1AA]">{p.domain} &middot; {p.target_language}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#555]">{p.contents_count || 0}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${p.status === 'active' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-[#2A2A2E] text-[#A1A1AA]'}`}>{p.status}</span>
                </div>
              </div>
            )) : <div className="px-5 py-8 text-center text-sm text-[#555]">No projects yet</div>}
          </div>
        </div>
        <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
          <div className="px-5 py-4 border-b border-[#2A2A2E]"><h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Recent Contents</h3></div>
          <div className="divide-y divide-[#2A2A2E]">
            {stats?.recent_contents?.length > 0 ? stats.recent_contents.map((c, i) => (
              <div key={c.id} className={`px-5 py-3 flex items-center justify-between animate-fade-up stagger-${i+1}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white font-medium truncate">{c.title}</div>
                  <div className="text-xs text-[#A1A1AA]">{c.duration || "N/A"} &middot; {c.views || 0} views</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ml-3 whitespace-nowrap ${c.translation_status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : c.translation_status === 'failed' ? 'bg-[#E53935]/10 text-[#E53935] border border-[#E53935]/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'}`}>{c.translation_status}</span>
              </div>
            )) : <div className="px-5 py-8 text-center text-sm text-[#555]">No content yet</div>}
          </div>
        </div>
      </div>

      {stats?.scrape_jobs?.length > 0 && (
        <div className="bg-[#141417] border border-[#2A2A2E] rounded-sm">
          <div className="px-5 py-4 border-b border-[#2A2A2E]"><h3 className="text-sm font-bold tracking-[0.1em] uppercase text-[#A1A1AA]">Recent Jobs</h3></div>
          <div className="divide-y divide-[#2A2A2E]">
            {stats.scrape_jobs.map(job => (
              <div key={job.id} className="px-5 py-3 flex items-center justify-between">
                <div>
                  <div className="text-sm text-white font-medium capitalize">{job.type}</div>
                  <div className="text-xs text-[#A1A1AA]">{new Date(job.created_at).toLocaleString()}</div>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-sm uppercase tracking-wider font-bold ${job.status === 'completed' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : job.status === 'running' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20' : 'bg-[#E53935]/10 text-[#E53935]'}`}>{job.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
